import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "../../css/OrderManagement.css";
import {fetchCompletedFeedbacks, storeCustomerFeedback} from "../../service/api/orders";
import { getUserRole } from "../../service/api/apiClient";

// ─── icons ────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);
const ExportIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);
const ResetIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="1 4 1 10 7 10"/>
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
    </svg>
);
const CalendarIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const ChevronLeft = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
);
const ChevronRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);
const StarIcon = ({ filled = true, size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
         fill={filled ? "#f59e0b" : "none"} stroke="#f59e0b"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
);


// ── SVG icons for attended status ────────────────────────────────────────────
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
const XIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

// ─── date helpers ─────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June",
    "July","August","September","October","November","December"];
const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const fromYMD = (s) => { const [y,m,d] = s.split("-"); return new Date(+y, +m-1, +d); };
const sameDay = (a, b) => a && b && toYMD(a) === toYMD(b);

// ─── DateRangePicker ──────────────────────────────────────────────────────────
const DateRangePicker = ({ fromDate, toDate, onChange }) => {
    const [open, setOpen]           = useState(false);
    const [hovered, setHovered]     = useState(null);
    const [selecting, setSelecting] = useState(null);
    const [popupStyle, setPopupStyle] = useState({});

    const today = new Date();
    const [viewYear,  setViewYear]  = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    const btnRef = useRef(null);
    const popRef = useRef(null);

    const openPopup = () => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setPopupStyle(spaceBelow >= 340
            ? { top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX }
            : { top: rect.top + window.scrollY - 348,  left: rect.left + window.scrollX }
        );
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current && !btnRef.current.contains(e.target) &&
                popRef.current  && !popRef.current.contains(e.target)) {
                setOpen(false); setSelecting(null);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y-1)) : setViewMonth(m => m-1);
    const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y+1)) : setViewMonth(m => m+1);

    const handleDayClick = (date) => {
        if (!selecting) {
            setSelecting(date);
            onChange({ from: toYMD(date), to: "" });
        } else {
            const [from, to] = date < selecting ? [date, selecting] : [selecting, date];
            onChange({ from: toYMD(from), to: toYMD(to) });
            setSelecting(null);
            setOpen(false);
        }
    };

    const buildDays = (year, month) => {
        const first = new Date(year, month, 1).getDay();
        const total = new Date(year, month + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < first; i++) cells.push(null);
        for (let d = 1; d <= total; d++) cells.push(new Date(year, month, d));
        return cells;
    };

    const fromD = fromDate ? fromYMD(fromDate) : null;
    const toD   = toDate   ? fromYMD(toDate)   : null;

    const dayStyle = (date) => {
        if (!date) return {};
        const [rFrom, rTo] = selecting
            ? (hovered && hovered < selecting ? [hovered, selecting] : [selecting, hovered])
            : [fromD, toD];
        const isStart = sameDay(date, fromD) || (selecting && sameDay(date, selecting));
        const isEnd   = !selecting && sameDay(date, toD);
        const inRange = rFrom && rTo && date > rFrom && date < rTo;
        const isToday = sameDay(date, today);
        if (isStart || isEnd) return { background:"#2563eb", color:"#fff", fontWeight:700, borderRadius:"50%" };
        if (inRange)          return { background:"#dbeafe", color:"#1e40af", borderRadius:0 };
        if (isToday)          return { fontWeight:700, borderRadius:"50%" };
        return { borderRadius:"50%" };
    };

    const cells = buildDays(viewYear, viewMonth);
    const label = fromDate && toDate ? `${fromDate}  →  ${toDate}`
        : fromDate ? `${fromDate}  →  …` : "Select date range";

    const popup = open && ReactDOM.createPortal(
        <div ref={popRef} style={{
            position:"absolute", ...popupStyle, zIndex:99999,
            background:"#fff", borderRadius:14,
            boxShadow:"0 8px 32px rgba(0,0,0,0.18)",
            padding:18, border:"1px solid #e2e8f0", width:260,
        }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <button onClick={prevMonth} style={{ border:"none", background:"none", cursor:"pointer", padding:4, color:"#64748b" }}><ChevronLeft/></button>
                <span style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>{MONTHS[viewMonth]} {viewYear}</span>
                <button onClick={nextMonth} style={{ border:"none", background:"none", cursor:"pointer", padding:4, color:"#64748b" }}><ChevronRight/></button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:4 }}>
                {DAYS.map(d => <div key={d} style={{ textAlign:"center", fontSize:10, color:"#94a3b8", fontWeight:600, padding:"2px 0" }}>{d}</div>)}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
                {cells.map((date, i) => (
                    <div key={i}
                         onClick={date ? () => handleDayClick(date) : undefined}
                         onMouseEnter={date ? () => setHovered(date) : undefined}
                         onMouseLeave={() => setHovered(null)}
                         style={{
                             height:32, display:"flex", alignItems:"center", justifyContent:"center",
                             fontSize:12, cursor: date ? "pointer" : "default", userSelect:"none",
                             ...(date ? dayStyle(date) : {}),
                         }}
                    >
                        {date ? date.getDate() : ""}
                    </div>
                ))}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, borderTop:"1px solid #f1f5f9", paddingTop:10 }}>
                <span style={{ fontSize:11, color:"#94a3b8" }}>
                    {selecting ? "Pick end date" : fromDate && toDate ? `${fromDate} → ${toDate}` : "Pick start date"}
                </span>
                <button onClick={() => { onChange({ from:"", to:"" }); setSelecting(null); setOpen(false); }}
                        style={{ fontSize:11, color:"#ef4444", background:"none", border:"none", cursor:"pointer", fontWeight:600 }}>
                    Clear
                </button>
            </div>
        </div>,
        document.body
    );

    return (
        <div style={{ position:"relative" }}>
            <button ref={btnRef} type="button"
                    onClick={() => open ? setOpen(false) : openPopup()}
                    style={{
                        display:"flex", alignItems:"center", gap:8,
                        padding:"8px 14px", borderRadius:10,
                        border:"1px solid #e2e8f0",
                        background: fromDate ? "#eff6ff" : "#fff",
                        color: fromDate ? "#2563eb" : "#64748b",
                        fontSize:13, cursor:"pointer", whiteSpace:"nowrap",
                        fontWeight: fromDate ? 600 : 400,
                    }}>
                <CalendarIcon/> {label}
            </button>
            {popup}
        </div>
    );
};


const SOURCE_OPTIONS = [
    { value: "delivery_guy", label: "Delivery Guy" },
    { value: "restaurant", label: "Restaurant" },
    { value: "order_manager", label: "Order Manager" },
    { value: "field_manager", label: "Field Manager" },
    { value: "custom", label: "Custom" },
];

const TARGET_OPTIONS = [
    { value: "order_manager", label: "Order Manager" },
    { value: "field_manager", label: "Field Manager" },
];

const PRIORITY_OPTIONS = [
    { value: "normal", label: "Normal" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
];

const emptyFeedbackItem = () => ({
    source: "",
    target: [],
    rating: "",
    review: "",
});

const isDoNotCallFeedback = (item) =>
    Boolean(
        item?.feedback_do_not_call_again ||
        item?.feedback_meta?.do_not_call_again ||
        item?.feedback_meta?.do_not_call
    );

const DoNotCallBadge = () => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            width: "fit-content",
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            background: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fcd34d",
            whiteSpace: "nowrap",
        }}
    >
        Do Not Call Again
    </span>
);



const StarRatingText = ({ rating }) => {
    if (rating === null || rating === undefined || rating === "") {
        return <span style={{ color: "#94a3b8", fontSize: 13 }}>No rating</span>;
    }

    const value = Number(rating);
    const fullStars = Math.max(0, Math.min(5, Math.round(value)));

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#f59e0b", letterSpacing: 1, fontSize: 14 }}>
                {"★".repeat(fullStars)}{"☆".repeat(5 - fullStars)}
            </span>
            <strong style={{ color: "#0f172a", fontSize: 13 }}>{value}/5</strong>
        </span>
    );
};

const ReviewBlock = ({ title, rating, review }) => {
    const hasRating = rating !== null && rating !== undefined && rating !== "";
    const hasReview = review && String(review).trim();

    if (!hasRating && !hasReview) return null;

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #fde68a",
                borderRadius: 12,
                padding: 12,
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 7 }}>
                {title}
            </div>

            <div style={{ marginBottom: 8 }}>
                <StarRatingText rating={rating} />
            </div>

            <div
                style={{
                    fontSize: 13,
                    color: hasReview ? "#334155" : "#94a3b8",
                    lineHeight: 1.45,
                    background: hasReview ? "#f8fafc" : "transparent",
                    border: hasReview ? "1px solid #e2e8f0" : "none",
                    borderRadius: hasReview ? 10 : 0,
                    padding: hasReview ? "8px 10px" : 0,
                    whiteSpace: "normal",
                }}
            >
                {hasReview ? `*** ${review}` : "No review"}
            </div>
        </div>
    );
};


const ExistingAppRatingsBlock = ({ deliveryRating }) => {
    const hasAnyRatingOrReview = deliveryRating && [
        deliveryRating.customer_to_store_rating,
        deliveryRating.customer_to_store_review,

        deliveryRating.customer_rating,
        deliveryRating.customer_review,

        deliveryRating.store_rating,
        deliveryRating.store_review,

        deliveryRating.delivery_to_customer_rating,
        deliveryRating.delivery_to_customer_review,
    ].some(v => v !== null && v !== undefined && String(v).trim() !== "");

    if (!hasAnyRatingOrReview) {
        return (
            <div
                style={{
                    marginBottom: 18,
                    padding: 14,
                    borderRadius: 14,
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    color: "#64748b",
                    fontSize: 13,
                    fontWeight: 600,
                }}
            >
                No app rating/review found for this order.
            </div>
        );
    }

    return (
        <div
            style={{
                marginBottom: 18,
                padding: 16,
                borderRadius: 14,
                border: "1px solid #fde68a",
                background: "#fffbeb",
            }}
        >
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#92400e",
                        textTransform: "uppercase",
                    }}
                >
                    Existing App Ratings / Reviews
                </div>

                <div style={{ fontSize: 13, color: "#78350f", marginTop: 2 }}>
                    Only for reference. Ask questions around these reviews if needed.
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                }}
            >
                <ReviewBlock
                    title="Customer → Store"
                    rating={deliveryRating.customer_to_store_rating}
                    review={deliveryRating.customer_to_store_review}
                />

                <ReviewBlock
                    title="Customer → Delivery Guy"
                    rating={deliveryRating.customer_rating}
                    review={deliveryRating.customer_review}
                />

                <ReviewBlock
                    title="Delivery Guy → Store"
                    rating={deliveryRating.store_rating}
                    review={deliveryRating.store_review}
                />

                <ReviewBlock
                    title="Delivery Guy → Customer"
                    rating={deliveryRating.delivery_to_customer_rating}
                    review={deliveryRating.delivery_to_customer_review}
                />
            </div>
        </div>
    );
};


const CustomerOrderSummaryBlock = ({ summary }) => {
    const successfulOrders = Number(summary?.successful_orders ?? 0);
    const failedOrders = Number(summary?.failed_orders ?? 0);
    const successfulOrderTotal = Number(summary?.successful_order_total ?? 0);

    const itemStyle = {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        fontWeight: 700,
        color: "#334155",
    };

    return (
        <div
            style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid #dbeafe",
                background: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 900, color: "#1d4ed8" }}>
                Customer Summary
            </div>

            <div style={itemStyle}>
                ✅ Success: <span style={{ color: "#15803d" }}>{successfulOrders}</span>
            </div>

            <div style={itemStyle}>
                ❌ Failed: <span style={{ color: "#dc2626" }}>{failedOrders}</span>
            </div>

            <div style={itemStyle}>
                💰 Total:{" "}
                <span style={{ color: "#0f172a" }}>
                    ₹{successfulOrderTotal.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                })}
                </span>
            </div>
        </div>
    );
};

const FeedbackModal = ({
                           open,
                           item,
                           mode = "edit",
                           attended,
                           submitting,
                           onClose,
                           onSubmit,
                       }) => {
    const isViewMode = mode === "view";

    const [mainRating, setMainRating] = useState("");
    const [mainReview, setMainReview] = useState("");
    const [isEscalated, setIsEscalated] = useState(false);
    const [priority, setPriority] = useState("normal");
    const [items, setItems] = useState([emptyFeedbackItem()]);
    const [doNotCallAgain, setDoNotCallAgain] = useState(false);

    const deliveryRating = item?.delivery_rating;
    const customerOrderSummary = item?.customer_order_summary;

    useEffect(() => {
        if (!open || !item) return;

        const meta = item?.feedback_meta || {};

        setMainRating(item?.rating ?? "");
        setMainReview(item?.feedback_payload ?? "");
        setIsEscalated(Boolean(meta?.is_escalated));
        setPriority(meta?.priority || "normal");
        setDoNotCallAgain(isDoNotCallFeedback(item));

        const metaItems = Array.isArray(meta?.items) ? meta.items : [];

        setItems(
            metaItems.length
                ? metaItems.map(row => ({
                    source: row.source || "",
                    target: Array.isArray(row.target) ? row.target : [],
                    rating: row.rating ?? "",
                    review: row.review || "",
                }))
                : [emptyFeedbackItem()]
        );
    }, [open, item?.id]);

    if (!open || !item) return null;

    const updateItem = (index, key, value) => {
        setItems(prev =>
            prev.map((row, i) =>
                i === index ? { ...row, [key]: value } : row
            )
        );
    };

    const toggleTarget = (index, targetValue) => {
        if (isViewMode) return;

        setItems(prev =>
            prev.map((row, i) => {
                if (i !== index) return row;

                const current = Array.isArray(row.target) ? row.target : [];
                const exists = current.includes(targetValue);

                return {
                    ...row,
                    target: exists
                        ? current.filter(v => v !== targetValue)
                        : [...current, targetValue],
                };
            })
        );
    };

    const addItem = () => {
        setItems(prev => [...prev, emptyFeedbackItem()]);
    };

    const removeItem = (index) => {
        setItems(prev =>
            prev.length === 1
                ? [emptyFeedbackItem()]
                : prev.filter((_, i) => i !== index)
        );
    };

    const formatRoleLabel = (value) => {
        if (!value) return "-";
        return String(value)
            .replaceAll("_", " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    const handleSubmit = () => {
        const cleanedItems = items
            .map(row => ({
                source: row.source,
                target: Array.isArray(row.target) ? row.target : [],
                rating:
                    row.rating !== "" &&
                    row.rating !== null &&
                    row.rating !== undefined
                        ? Number(row.rating)
                        : null,
                review: row.review?.trim() ?? "",
            }))
            .filter(row =>
                row.source ||
                row.target.length ||
                row.rating !== null ||
                row.review
            );

        if (!mainReview.trim() && cleanedItems.length === 0) {
            alert("Please enter feedback or add at least one role-based feedback.");
            return;
        }

        const invalidItem = cleanedItems.find(row => !row.source);
        if (invalidItem) {
            alert("Please select source for every role feedback item.");
            return;
        }

        onSubmit({
            rating: mainRating !== "" ? Number(mainRating) : null,
            feedback_payload: mainReview.trim(),
            feedback_meta: {
                ...(item?.feedback_meta || {}),
                is_escalated: Boolean(isEscalated),
                priority: isEscalated ? priority : "normal",
                items: cleanedItems,
                do_not_call_again: Boolean(doNotCallAgain),
                do_not_call: Boolean(doNotCallAgain),
            },
        });
    };

    const fieldStyle = {
        marginTop: 6,
        width: "100%",
        padding: "9px 10px",
        borderRadius: 10,
        border: "1px solid #cbd5e1",
        outline: "none",
        background: isViewMode ? "#f8fafc" : "#fff",
        color: "#0f172a",
        fontSize: 14,
        fontFamily: "inherit",
    };

    return ReactDOM.createPortal(
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 23, 42, 0.55)",
                zIndex: 999999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
            }}
            onMouseDown={() => {
                if (!submitting) onClose();
            }}
        >
            <div
                onMouseDown={e => e.stopPropagation()}
                style={{
                    width: "min(920px, 96vw)",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    background: "#fff",
                    borderRadius: 18,
                    boxShadow: "0 24px 80px rgba(15,23,42,0.35)",
                    border: "1px solid #e2e8f0",
                }}
            >
                {/* HEADER */}
                <div
                    style={{
                        padding: "18px 22px",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 14,
                        alignItems: "flex-start",
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#64748b",
                                textTransform: "uppercase",
                            }}
                        >
                            {isViewMode ? "View Customer Feedback" : "Edit Customer Feedback"}
                        </div>

                        <h2
                            style={{
                                margin: "4px 0 0",
                                fontSize: 20,
                                color: "#0f172a",
                            }}
                        >
                            #{item?.unique_order_id?.slice(-4)} · {item?.user?.name ?? "-"}
                        </h2>

                        <div
                            style={{
                                marginTop: 5,
                                fontSize: 13,
                                color: "#64748b",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                            }}
                        >
                            <span>{item?.user?.phone ?? "-"} · {attended ? "Attended" : "No Answer"}</span>
                            {doNotCallAgain && <DoNotCallBadge />}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            border: "none",
                            background: "#f1f5f9",
                            color: "#334155",
                            borderRadius: 10,
                            padding: "8px 11px",
                            cursor: submitting ? "not-allowed" : "pointer",
                            fontWeight: 700,
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div style={{ padding: 22 }}>
                    <CustomerOrderSummaryBlock summary={customerOrderSummary} />
                    <ExistingAppRatingsBlock deliveryRating={deliveryRating} />

                    {/* MAIN FEEDBACK */}
                    <div
                        style={{
                            marginBottom: 18,
                            padding: 16,
                            borderRadius: 14,
                            border: "1px solid #e2e8f0",
                            background: "#fff",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 14,
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 900,
                                        color: "#334155",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Main Feedback
                                </div>

                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#64748b",
                                        marginTop: 2,
                                    }}
                                >
                                    Overall customer feedback for this completed order.
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "5px 10px",
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 800,
                                    background: attended ? "#f0fdf4" : "#fef2f2",
                                    color: attended ? "#15803d" : "#dc2626",
                                    border: attended ? "1px solid #bbf7d0" : "1px solid #fecaca",
                                }}
                            >
                                {attended ? "✅ Attended" : "❌ No Answer"}
                            </div>
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "120px 1fr",
                                gap: 14,
                            }}
                        >
                            <div>
                                <label
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#475569",
                                    }}
                                >
                                    Rating
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    max="5"
                                    step="0.1"
                                    placeholder="0–5"
                                    disabled={isViewMode}
                                    value={mainRating}
                                    onChange={e => setMainRating(e.target.value)}
                                    style={fieldStyle}
                                />
                            </div>

                            <div>
                                <label
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#475569",
                                    }}
                                >
                                    Feedback
                                </label>

                                <textarea
                                    disabled={isViewMode}
                                    rows={3}
                                    placeholder="Customer feedback summary..."
                                    value={mainReview}
                                    onChange={e => setMainReview(e.target.value)}
                                    style={{
                                        ...fieldStyle,
                                        resize: "vertical",
                                    }}
                                />
                            </div>
                        </div>
                    </div>


                    <div
                        style={{
                            marginBottom: 18,
                            padding: 14,
                            borderRadius: 14,
                            border: doNotCallAgain ? "1px solid #fcd34d" : "1px solid #e2e8f0",
                            background: doNotCallAgain ? "#fffbeb" : "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 14,
                            flexWrap: "wrap",
                        }}
                    >
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                cursor: isViewMode ? "default" : "pointer",
                                fontSize: 14,
                                fontWeight: 800,
                                color: doNotCallAgain ? "#92400e" : "#334155",
                            }}
                        >
                            <input
                                disabled={isViewMode}
                                type="checkbox"
                                checked={doNotCallAgain}
                                onChange={e => setDoNotCallAgain(e.target.checked)}
                                style={{
                                    width: 17,
                                    height: 17,
                                    accentColor: "#f59e0b",
                                }}
                            />
                            Do Not Call Again
                        </label>

                        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                            Customer will be excluded from future feedback calls.
                        </span>
                    </div>
                    {/* ESCALATION */}
                    <div
                        style={{
                            marginBottom: 18,
                            padding: 14,
                            borderRadius: 14,
                            border: isEscalated ? "1px solid #fecaca" : "1px solid #e2e8f0",
                            background: isEscalated ? "#fef2f2" : "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 14,
                            flexWrap: "wrap",
                        }}
                    >
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                cursor: isViewMode ? "default" : "pointer",
                                fontSize: 14,
                                fontWeight: 800,
                                color: isEscalated ? "#b91c1c" : "#334155",
                            }}
                        >
                            <input
                                disabled={isViewMode}
                                type="checkbox"
                                checked={isEscalated}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setIsEscalated(checked);
                                    setPriority(checked ? "high" : "normal");
                                }}
                                style={{
                                    width: 17,
                                    height: 17,
                                    accentColor: "#ef4444",
                                }}
                            />

                            {isEscalated ? "Escalated feedback" : "Normal feedback"}
                        </label>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                    color: "#64748b",
                                }}
                            >
                                Priority
                            </span>

                            <select
                                value={priority}
                                disabled={!isEscalated || isViewMode}
                                onChange={e => setPriority(e.target.value)}
                                style={{
                                    minWidth: 140,
                                    padding: "8px 12px",
                                    borderRadius: 10,
                                    border: "1px solid #cbd5e1",
                                    background: !isEscalated || isViewMode ? "#f1f5f9" : "#fff",
                                    color: !isEscalated ? "#94a3b8" : "#0f172a",
                                    fontWeight: 700,
                                    outline: "none",
                                }}
                            >
                                {PRIORITY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ROLE FEEDBACK */}
                    <div
                        style={{
                            marginBottom: 4,
                            padding: 16,
                            borderRadius: 14,
                            border: "1px solid #e2e8f0",
                            background: "#f8fafc",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 14,
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 900,
                                        color: "#334155",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Role Based Feedback
                                </div>

                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#64748b",
                                        marginTop: 2,
                                    }}
                                >
                                    Source, responsible role, rating and detailed reason.
                                </div>
                            </div>

                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={addItem}
                                    style={{
                                        border: "1px solid #bfdbfe",
                                        background: "#eff6ff",
                                        color: "#2563eb",
                                        fontWeight: 800,
                                        borderRadius: 10,
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                    }}
                                >
                                    + Add Role
                                </button>
                            )}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                            }}
                        >
                            {items.map((row, index) => (
                                <div
                                    key={index}
                                    style={{
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 14,
                                        padding: 14,
                                        background: "#fff",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: isViewMode
                                                ? "1fr 110px 1.4fr"
                                                : "1fr 110px 1.4fr 36px",
                                            gap: 12,
                                            alignItems: "start",
                                        }}
                                    >
                                        <div>
                                            <label
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: "#475569",
                                                }}
                                            >
                                                Source
                                            </label>

                                            <select
                                                disabled={isViewMode}
                                                value={row.source}
                                                onChange={e => updateItem(index, "source", e.target.value)}
                                                style={fieldStyle}
                                            >
                                                <option value="">Select source</option>
                                                {SOURCE_OPTIONS.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: "#475569",
                                                }}
                                            >
                                                Rating
                                            </label>

                                            <input
                                                disabled={isViewMode}
                                                type="number"
                                                min="0"
                                                max="5"
                                                step="0.1"
                                                placeholder="0–5"
                                                value={row.rating}
                                                onChange={e => updateItem(index, "rating", e.target.value)}
                                                style={fieldStyle}
                                            />
                                        </div>

                                        <div>
                                            <label
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: "#475569",
                                                }}
                                            >
                                                Target / Responsible
                                            </label>

                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 8,
                                                }}
                                            >
                                                {TARGET_OPTIONS.map(opt => {
                                                    const checked = row.target?.includes(opt.value);

                                                    if (isViewMode && !checked) return null;

                                                    return (
                                                        <label
                                                            key={opt.value}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 6,
                                                                padding: "6px 9px",
                                                                borderRadius: 999,
                                                                border: checked
                                                                    ? "1px solid #2563eb"
                                                                    : "1px solid #cbd5e1",
                                                                background: checked ? "#eff6ff" : "#fff",
                                                                color: checked ? "#1d4ed8" : "#475569",
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                cursor: isViewMode ? "default" : "pointer",
                                                            }}
                                                        >
                                                            {!isViewMode && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => toggleTarget(index, opt.value)}
                                                                    style={{ accentColor: "#2563eb" }}
                                                                />
                                                            )}

                                                            {opt.label}
                                                        </label>
                                                    );
                                                })}

                                                {isViewMode && (!row.target || row.target.length === 0) && (
                                                    <span
                                                        style={{
                                                            fontSize: 13,
                                                            color: "#94a3b8",
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        No target selected
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {!isViewMode && (
                                            <button
                                                type="button"
                                                onClick={() => removeItem(index)}
                                                style={{
                                                    marginTop: 22,
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: 10,
                                                    border: "1px solid #fecaca",
                                                    background: "#fef2f2",
                                                    color: "#dc2626",
                                                    cursor: "pointer",
                                                    fontWeight: 900,
                                                }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>

                                    <div style={{ marginTop: 12 }}>
                                        <label
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: "#475569",
                                            }}
                                        >
                                            Review
                                        </label>

                                        <textarea
                                            disabled={isViewMode}
                                            rows={2}
                                            placeholder="Example: Rider delayed pickup / Restaurant preparation delay..."
                                            value={row.review}
                                            onChange={e => updateItem(index, "review", e.target.value)}
                                            style={{
                                                ...fieldStyle,
                                                resize: "vertical",
                                                minHeight: 74,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <div
                    style={{
                        padding: "16px 22px",
                        borderTop: "1px solid #e2e8f0",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        background: "#f8fafc",
                        borderBottomLeftRadius: 18,
                        borderBottomRightRadius: 18,
                    }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="btn"
                    >
                        {isViewMode ? "Close" : "Cancel"}
                    </button>

                    {!isViewMode && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="btn"
                            style={{
                                background: "#2563eb",
                                color: "#fff",
                                border: "none",
                                opacity: submitting ? 0.65 : 1,
                                cursor: submitting ? "not-allowed" : "pointer",
                            }}
                        >
                            {submitting ? "Submitting…" : "Submit Feedback"}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

// ─── main component ───────────────────────────────────────────────────────────
const CompletedFeedbacks = () => {

    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const PER_PAGE = 100;

    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    // ── NEW: attended filter ("" = all, "1" = attended, "0" = not attended) ───
    const [attendedFilter, setAttendedFilter] = useState("");


    const [sourceFilter, setSourceFilter] = useState("");
    const [targetFilter, setTargetFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [escalatedFilter, setEscalatedFilter] = useState("");
    const [doNotCallFilter, setDoNotCallFilter] = useState("exclude");
    const role = getUserRole();
    const canManageDnc =
        role === "admin" ||
        role === "manager";
    const isCustomerFeedback =
        role === "customer_feedback";

    console.log("Current role:", role);
    console.log("canManageDnc:", canManageDnc);

    const [feedbackModal, setFeedbackModal] = useState({
        open: false,
        item: null,
        mode: "edit", // "view" | "edit"
    });

    const [submitting, setSubmitting] = useState({});

    const loadData = async (page = 1, opts = {}) => {
        try {
            setLoading(true);

            const resolvedSearch = "search" in opts ? opts.search : search;
            const resolvedFromDate = "from_date" in opts ? opts.from_date : fromDate;
            const resolvedToDate = "to_date" in opts ? opts.to_date : toDate;
            const resolvedAttended = "is_attended" in opts ? opts.is_attended : attendedFilter;

            const resolvedSource = "source" in opts ? opts.source : sourceFilter;
            const resolvedTarget = "target" in opts ? opts.target : targetFilter;
            const resolvedPriority = "priority" in opts ? opts.priority : priorityFilter;
            const resolvedEscalated = "is_escalated" in opts ? opts.is_escalated : escalatedFilter;
            const resolvedDoNotCall = canManageDnc && !isCustomerFeedback
                ? ("do_not_call_filter" in opts ? opts.do_not_call_filter : doNotCallFilter)
                : "exclude";

            const params = {
                page,
                per_page: PER_PAGE,
                ...(resolvedSearch ? {search: resolvedSearch} : {}),
                ...(resolvedFromDate ? {from_date: resolvedFromDate} : {}),
                ...(resolvedToDate ? {to_date: resolvedToDate} : {}),
                ...(resolvedAttended !== "" ? {is_attended: resolvedAttended} : {}),

                ...(resolvedSource ? {source: resolvedSource} : {}),
                ...(resolvedTarget ? {target: resolvedTarget} : {}),
                ...(resolvedPriority ? {priority: resolvedPriority} : {}),
                ...(resolvedEscalated !== "" ? {is_escalated: resolvedEscalated} : {}),
                do_not_call_filter: resolvedDoNotCall,
            };

            const response = await fetchCompletedFeedbacks(params);
            const paginated = response?.data ?? {};

            setFeedbacks(paginated?.data ?? []);
            setCurrentPage(paginated?.current_page ?? 1);
            setLastPage(paginated?.last_page ?? 1);
            setTotal(paginated?.total ?? 0);
        } catch (err) {
            console.error(err);
            alert("Failed to fetch completed feedbacks");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData(1, {search: "", from_date: "", to_date: "", is_attended: "", do_not_call_filter: "exclude"});
    }, []);

    const handleDateChange = ({from, to}) => {
        setFromDate(from);
        setToDate(to);
        loadData(1, {from_date: from, to_date: to});
    };

    const handleSearch = () =>
        loadData(1, {
            search,
            from_date: fromDate,
            to_date: toDate,
            is_attended: attendedFilter,
            source: sourceFilter,
            target: targetFilter,
            priority: priorityFilter,
            is_escalated: escalatedFilter,
            do_not_call_filter: doNotCallFilter,
        });


    const handleUpdateFeedback = async (payload) => {
        const item = feedbackModal.item;
        if (!item) return;

        try {
            setSubmitting(p => ({...p, [item.id]: true}));

            await storeCustomerFeedback({
                order_id: item.id,
                user_id: item?.user?.id,

                rating: payload.rating,
                feedback_payload: payload.feedback_payload,
                is_attended: 1,

                feedback_meta: payload.feedback_meta,
            });

            setFeedbackModal({open: false, item: null});
            await loadData(currentPage);

            alert("Feedback updated successfully!");
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "Failed to update feedback.");
        } finally {
            setSubmitting(p => ({...p, [item.id]: false}));
        }
    };


    const handleReset = () => {
        setSearch("");
        setFromDate("");
        setToDate("");
        setAttendedFilter("");

        setSourceFilter("");
        setTargetFilter("");
        setPriorityFilter("");
        setEscalatedFilter("");
        setDoNotCallFilter("exclude");

        loadData(1, {
            search: "",
            from_date: "",
            to_date: "",
            is_attended: "",
            source: "",
            target: "",
            priority: "",
            is_escalated: "",
            do_not_call_filter: "exclude",
        });
    };

    const goTo = (page) => {
        if (page >= 1 && page <= lastPage) {
            loadData(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const formatDate = (str) => str
        ? new Date(str).toLocaleString("en-IN", { hour12: true })
        : "-";

    const handleExport = () => {
        const headers = ["Order ID","Customer Name","Customer Phone",
            "Restaurant","Delivery Partner","Items","Total Time",
            "Price","Payment","Rating","Feedback","Called At","Is Attended"];
        const rows = feedbacks.map(item => [
            item?.id ?? item?.order_id,
            item?.user?.name ?? "",
            item?.user?.phone ?? "",
            item?.restaurant?.name ?? "",
            item?.delivery_guy?.name ?? "",
            item?.items?.map(f => f.name).join("; ") ?? "",
            item?.delivery_time ?? "",
            item?.total ?? 0,
            item?.payment_mode ?? "",
            item?.rating ?? "",
            item?.feedback_payload ?? "",
            item?.called_at ?? "",
            Boolean(item?.is_attended) ? "Attended" : "Not Attended",
        ]);
        const csv  = [headers,...rows].map(r => r.map(v => `"${String(v??'').replace(/"/g,'""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type:"text/csv" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href=url; a.download="completed-feedbacks.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="order-page">

            {/* HEADER */}
            <div className="page-header">
                <div>
                    <p className="page-header__eyebrow">Customer Support</p>
                    <h1 className="page-header__title">
                        Completed Feedbacks
                        {total > 0 && (
                            <span style={{fontSize: 14, fontWeight: 400, marginLeft: 10, color: "#64748b"}}>
                                ({total.toLocaleString()} records)
                            </span>
                        )}
                    </h1>
                </div>
                <div className="page-header__actions">
                    <button className="btn" onClick={handleExport}><ExportIcon/> Export CSV</button>
                    <button className="btn" onClick={handleReset}
                            style={{color: "#ef4444", borderColor: "#fca5a5", background: "#fef2f2"}}>
                        <ResetIcon/> Reset Filters
                    </button>
                </div>
            </div>

            {/* FILTERS */}
            <div className="card" style={{marginBottom: 20, padding: 16}}>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 14,
                    }}
                >
                    <div className="om-search-wrap" style={{flex: "1 1 220px"}}>
                    <span className="om-search-icon">
                        <SearchIcon/>
                    </span>

                        <input
                            className="om-search-input"
                            placeholder="Search customer / order ID / phone"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && handleSearch()}
                        />
                    </div>

                    <DateRangePicker
                        fromDate={fromDate}
                        toDate={toDate}
                        onChange={handleDateChange}
                    />

                    <select
                        value={attendedFilter}
                        onChange={e => {
                            const val = e.target.value;
                            setAttendedFilter(val);
                            loadData(1, {is_attended: val});
                        }}
                        style={{
                            padding: "8px 14px",
                            borderRadius: 10,
                            border: "1px solid #e2e8f0",
                            background: attendedFilter !== "" ? "#f0fdf4" : "#fff",
                            color: attendedFilter !== "" ? "#15803d" : "#64748b",
                            fontSize: 13,
                            cursor: "pointer",
                            fontWeight: attendedFilter !== "" ? 600 : 400,
                            outline: "none",
                            minWidth: 135,
                        }}
                    >
                        <option value="">All Calls</option>
                        <option value="1">✅ Attended</option>
                        <option value="0">❌ Not Attended</option>
                    </select>

                    <select
                        value={sourceFilter}
                        onChange={e => {
                            const val = e.target.value;
                            setSourceFilter(val);
                            loadData(1, {source: val});
                        }}
                        style={{
                            padding: "8px 14px",
                            borderRadius: 10,
                            border: "1px solid #e2e8f0",
                            background: sourceFilter ? "#eff6ff" : "#fff",
                            color: sourceFilter ? "#2563eb" : "#64748b",
                            fontSize: 13,
                            cursor: "pointer",
                            fontWeight: sourceFilter ? 600 : 400,
                            outline: "none",
                            minWidth: 140,
                        }}
                    >
                        <option value="">All Sources</option>
                        <option value="delivery_guy">Delivery Guy</option>
                        <option value="restaurant">Restaurant</option>
                        <option value="order_manager">Order Manager</option>
                        <option value="field_manager">Field Manager</option>
                        <option value="customer">Customer</option>
                        <option value="custom">Custom</option>
                    </select>

                    <select
                        value={targetFilter}
                        onChange={e => {
                            const val = e.target.value;
                            setTargetFilter(val);
                            loadData(1, {target: val});
                        }}
                        style={{
                            padding: "8px 14px",
                            borderRadius: 10,
                            border: "1px solid #e2e8f0",
                            background: targetFilter ? "#eff6ff" : "#fff",
                            color: targetFilter ? "#2563eb" : "#64748b",
                            fontSize: 13,
                            cursor: "pointer",
                            fontWeight: targetFilter ? 600 : 400,
                            outline: "none",
                            minWidth: 140,
                        }}
                    >
                        <option value="">All Targets</option>
                        <option value="order_manager">Order Manager</option>
                        <option value="field_manager">Field Manager</option>
                        <option value="admin">Admin</option>
                        <option value="restaurant">Restaurant</option>
                        <option value="delivery_guy">Delivery Guy</option>
                        <option value="customer">Customer</option>
                    </select>

                    <select
                        value={priorityFilter}
                        onChange={e => {
                            const val = e.target.value;
                            setPriorityFilter(val);
                            loadData(1, {priority: val});
                        }}
                        style={{
                            padding: "8px 14px",
                            borderRadius: 10,
                            border: "1px solid #e2e8f0",
                            background: priorityFilter ? "#fef3c7" : "#fff",
                            color: priorityFilter ? "#92400e" : "#64748b",
                            fontSize: 13,
                            cursor: "pointer",
                            fontWeight: priorityFilter ? 600 : 400,
                            outline: "none",
                            minWidth: 135,
                        }}
                    >
                        <option value="">All Priority</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                    </select>


                    {canManageDnc && (
                        <select
                            value={doNotCallFilter}
                            onChange={e => {
                                const val = e.target.value;
                                setDoNotCallFilter(val);
                                loadData(1, {do_not_call_filter: val});
                            }}
                            style={{
                                padding: "8px 14px",
                                borderRadius: 10,
                                border: "1px solid #e2e8f0",
                                background: doNotCallFilter !== "exclude" ? "#fef3c7" : "#fff",
                                color: doNotCallFilter !== "exclude" ? "#92400e" : "#64748b",
                                fontSize: 13,
                                cursor: "pointer",
                                fontWeight: doNotCallFilter !== "exclude" ? 600 : 400,
                                outline: "none",
                                minWidth: 190,
                            }}
                        >
                            <option value="exclude">Exclude Do Not Call</option>
                            <option value="include">Include Do Not Call</option>
                            <option value="only">Only Do Not Call</option>
                        </select>
                    )}
                    <button className="btn" onClick={handleSearch}>
                        Search
                    </button>
                </div>

                {(fromDate ||
                    toDate ||
                    attendedFilter !== "" ||
                    sourceFilter ||
                    targetFilter ||
                    priorityFilter ||
                    (canManageDnc && doNotCallFilter !== "exclude") ||
                    escalatedFilter !== "") && (
                    <div
                        style={{
                            marginTop: 12,
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        {fromDate && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    From: {fromDate}
                </span>
                        )}

                        {toDate && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    To: {toDate}
                </span>
                        )}

                        {attendedFilter !== "" && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#f0fdf4",
                                    color: "#15803d",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    {attendedFilter === "1"
                        ? "✅ Attended only"
                        : "❌ Not Attended only"}
                </span>
                        )}

                        {sourceFilter && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    Source: {sourceFilter.replaceAll("_", " ")}
                </span>
                        )}

                        {targetFilter && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    Target: {targetFilter.replaceAll("_", " ")}
                </span>
                        )}

                        {priorityFilter && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#fef3c7",
                                    color: "#92400e",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    Priority: {priorityFilter}
                </span>
                        )}



                        {canManageDnc && doNotCallFilter !== "exclude" && (
                            <span
                                style={{
                                    fontSize: 12,
                                    background: "#fef3c7",
                                    color: "#92400e",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontWeight: 600,
                                }}
                            >
                    {doNotCallFilter === "only" ? "Only Do Not Call" : "Include Do Not Call"}
                </span>
                        )}
                    </div>
                )}
            </div>
            {/* TABLE */}
            <div className="card" style={{overflow: "hidden", padding: 0}}>
                <div className="om-table-wrap">
                    <table className="om-table">
                        <thead className="om-thead">
                        <tr>
                            <th className="om-th">Customer</th>
                            <th className="om-th">Order ID</th>
                            <th className="om-th">Restaurant</th>
                            <th className="om-th">Delivery Partner</th>
                            <th className="om-th">Items</th>
                            <th className="om-th">Total Time</th>
                            <th className="om-th">Price</th>
                            <th className="om-th">Payment</th>
                            <th className="om-th">Rating</th>
                            <th className="om-th">Feedback</th>
                            <th className="om-th">Called At</th>
                            <th className="om-th">Attended</th>
                            <th className="om-th">Source</th>
                            <th className="om-th">Target</th>
                            <th className="om-th">Priority</th>
                            <th className="om-th">Action</th>
                        </tr>
                        </thead>
                        <tbody className="om-tbody">
                        {loading ? (
                            <tr>
                                <td colSpan={16}>
                                    <div className="om-empty">Loading…</div>
                                </td>
                            </tr>
                        ) : feedbacks.length === 0 ? (
                            <tr>
                                <td colSpan={16}>
                                    <div className="om-empty">No completed feedbacks found.</div>
                                </td>
                            </tr>
                        ) : (
                            feedbacks.map(item => (
                                <tr key={item.id ?? item.order_id}>

                                    {/* CUSTOMER */}
                                    <td className="om-td">
                                        <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                                            <strong>{item?.user?.name ?? "-"}</strong>
                                            <span style={{
                                                fontSize: 12,
                                                color: "#64748b"
                                            }}>{item?.user?.phone ?? "-"}</span>
                                            {isDoNotCallFeedback(item) && <DoNotCallBadge />}
                                        </div>
                                    </td>

                                    {/* ORDER ID */}
                                    <td className="om-td">
                                        <a href={`https://dashboard.totalfresh.co/order/${item?.unique_order_id}`}
                                           target="_blank" rel="noopener noreferrer"
                                           style={{color: "#2563eb", fontWeight: 700, textDecoration: "none"}}>
                                            #{item?.unique_order_id?.slice(-4)}
                                        </a>
                                    </td>

                                    {/* RESTAURANT */}
                                    <td className="om-td">{item?.restaurant?.name ?? "-"}</td>

                                    {/* DELIVERY PARTNER */}
                                    <td className="om-td">{item?.delivery_guy?.name ?? "Not Assigned"}</td>

                                    {/* ITEMS */}
                                    <td className="om-td">
                                        <div style={{
                                            maxWidth: 200,
                                            whiteSpace: "normal",
                                            fontSize: 12,
                                            lineHeight: 1.4
                                        }}>
                                            {item?.items?.map(f => f.name).join(", ") ?? "-"}
                                        </div>
                                    </td>

                                    {/* TOTAL TIME */}
                                    <td className="om-td">{item?.total_time ?? "-"}</td>

                                    {/* PRICE */}
                                    <td className="om-td" style={{fontWeight: 600}}>₹{item?.total_price ?? 0}</td>

                                    {/* PAYMENT */}
                                    <td className="om-td">
                                        <span style={{
                                            fontSize: 11, padding: "2px 7px", borderRadius: 10, fontWeight: 600,
                                            background: item?.payment_mode === "COD" ? "#e0f2fe" : "#f0fdf4",
                                            color: item?.payment_mode === "COD" ? "#0369a1" : "#15803d",
                                        }}>
                                            {item?.payment_mode?.toUpperCase() ?? "-"}
                                        </span>
                                    </td>

                                    {/* RATING */}
                                    <td className="om-td">
                                        <div style={{display: "flex", alignItems: "center", gap: 4}}>
                                            {item?.rating != null ? (
                                                <>
                                                    <StarIcon filled size={14}/>
                                                    <span>{item.rating}</span>
                                                </>
                                            ) : (
                                                <span style={{color: "#94a3b8"}}>—</span>
                                            )}
                                        </div>
                                    </td>

                                    {/* FEEDBACK */}
                                    <td className="om-td">
                                        <div
                                            style={{
                                                maxWidth: 220,
                                                whiteSpace: "normal",
                                                fontSize: 13,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {item?.feedback_payload ?? "-"}
                                        </div>
                                    </td>

                                    {/* CALLED AT */}
                                    <td className="om-td" style={{fontSize: 12, whiteSpace: "nowrap"}}>
                                        {formatDate(item?.called_at)}
                                    </td>

                                    {/* ATTENDED */}
                                    <td className="om-td" style={{textAlign: "center"}}>
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 5,
                                            padding: "4px 10px",
                                            borderRadius: 8,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            background: Boolean(item.is_attended) ? "#f0fdf4" : "#fef2f2",
                                            color: Boolean(item.is_attended) ? "#15803d" : "#dc2626",
                                            border:
                                                Boolean(item.is_attended)
                                                    ? "1.5px solid #86efac"
                                                    : "1.5px solid #fca5a5",
                                        }}
                                    >
                                        {Boolean(item.is_attended) ? (
                                            <>
                                                <CheckIcon/> Attended
                                            </>
                                        ) : (
                                            <>
                                                <XIcon/> Not Attended
                                            </>
                                        )}
                                    </span>
                                    </td>

                                    <td className="om-td">
                                        {item?.role_feedback_items?.length
                                            ? item.role_feedback_items
                                                .map(r => r.source?.replaceAll("_", " "))
                                                .filter(Boolean)
                                                .join(", ")
                                            : "-"
                                        }
                                    </td>

                                    <td className="om-td">
                                        {item?.role_feedback_items?.length
                                            ? item.role_feedback_items
                                                .flatMap(r => Array.isArray(r.target) ? r.target : [])
                                                .filter(Boolean)
                                                .map(r => r.replaceAll("_", " "))
                                                .join(", ")
                                            : "-"
                                        }
                                    </td>


                                    {/* PRIORITY */}
                                    <td className="om-td">
                                    <span
                                        style={{
                                            fontSize: 11,
                                            padding: "3px 8px",
                                            borderRadius: 999,
                                            fontWeight: 800,
                                            textTransform: "capitalize",
                                            background:
                                                item?.feedback_meta?.priority === "urgent"
                                                    ? "#fee2e2"
                                                    : item?.feedback_meta?.priority === "high"
                                                        ? "#fef3c7"
                                                        : "#f1f5f9",
                                            color:
                                                item?.feedback_meta?.priority === "urgent"
                                                    ? "#b91c1c"
                                                    : item?.feedback_meta?.priority === "high"
                                                        ? "#92400e"
                                                        : "#475569",
                                        }}
                                    >
                                        {item?.feedback_meta?.is_escalated
                                            ? item?.feedback_meta?.priority || "high"
                                            : "normal"}
                                    </span>
                                    </td>


                                    <td className="om-td">
                                        <div style={{display: "flex", gap: 8}}>
                                            <button
                                                className="btn"
                                                onClick={() =>
                                                    setFeedbackModal({
                                                        open: true,
                                                        item,
                                                        mode: "view",
                                                    })
                                                }
                                                style={{
                                                    background: "#f8fafc",
                                                    color: "#334155",
                                                    border: "1px solid #cbd5e1",
                                                }}
                                            >
                                                View
                                            </button>

                                            <button
                                                className="btn"
                                                onClick={() =>
                                                    setFeedbackModal({
                                                        open: true,
                                                        item,
                                                        mode: "edit",
                                                    })
                                                }
                                                style={{
                                                    background: "#2563eb",
                                                    color: "#fff",
                                                    border: "none",
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    </td>

                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {!loading && lastPage > 1 && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 8,
                        padding: "12px 16px",
                        borderTop: "1px solid #f1f5f9"
                    }}>
                        <button className="btn" onClick={() => goTo(currentPage - 1)} disabled={currentPage === 1}>←
                            Prev
                        </button>
                        <span style={{fontSize: 13, color: "#64748b"}}>Page {currentPage} of {lastPage}</span>
                        <button className="btn" onClick={() => goTo(currentPage + 1)}
                                disabled={currentPage === lastPage}>Next →
                        </button>
                    </div>
                )}


                <FeedbackModal
                    open={feedbackModal.open}
                    item={feedbackModal.item}
                    mode={feedbackModal.mode}
                    attended={feedbackModal.item ? Boolean(feedbackModal.item.is_attended) : true}
                    submitting={feedbackModal.item ? submitting[feedbackModal.item.id] : false}
                    onClose={() => {
                        if (feedbackModal.item && submitting[feedbackModal.item.id]) return;
                        setFeedbackModal({ open: false, item: null, mode: "edit" });
                    }}
                    onSubmit={handleUpdateFeedback}
                />
            </div>
        </div>
    );
};

export default CompletedFeedbacks;